import React, { useState, useEffect, useRef } from 'react';
import { Flame, Heart, Music, Send, MessageCircle } from 'lucide-react';

/* ═══════════════════════════════════════════════════════════════════════════════
   PULSE LIVE — CHAT & REACTIONS (TikTok Style)
   
   Chat: Semi-transparent fog effect, messages float up
   Reactions: Hearts, fire, and musical notes float up
═══════════════════════════════════════════════════════════════════════════════ */

/* ── Floating Reaction Animation ── */
const FloatingReaction = ({ type, id, onComplete }) => {
  const icons = {
    heart: { Icon: Heart, color: '#ff4d6d', fill: true },
    fire: { Icon: Flame, color: '#ff9500', fill: false },
    music: { Icon: Music, color: '#d4a853', fill: false },
  };
  
  const { Icon, color, fill } = icons[type] || icons.heart;
  
  // Random horizontal offset for organic feel
  const randomX = useRef(Math.random() * 60 - 30); // -30 to +30
  const randomDuration = useRef(2 + Math.random() * 1); // 2-3s
  const randomDelay = useRef(Math.random() * 0.2);
  
  useEffect(() => {
    const timer = setTimeout(onComplete, randomDuration.current * 1000 + 500);
    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <div
      className="absolute bottom-0 right-4 pointer-events-none"
      style={{
        animation: `floatUpReaction ${randomDuration.current}s ease-out ${randomDelay.current}s forwards`,
        '--random-x': `${randomX.current}px`,
      }}
    >
      <Icon 
        size={28} 
        color={color} 
        fill={fill ? color : 'none'}
        className="drop-shadow-lg"
        style={{ filter: `drop-shadow(0 0 8px ${color}50)` }}
      />
    </div>
  );
};

/* ── Reactions Container ── */
export const LiveReactionsOverlay = ({ onReact, token, disabled }) => {
  const [reactions, setReactions] = useState([]);
  const reactionId = useRef(0);

  const addReaction = (type) => {
    if (!token || disabled) return;
    
    const id = reactionId.current++;
    setReactions(prev => [...prev, { id, type }]);
    onReact?.(type);
  };

  const removeReaction = (id) => {
    setReactions(prev => prev.filter(r => r.id !== id));
  };

  const buttons = [
    { type: 'heart', Icon: Heart, color: 'text-pink-400 hover:text-pink-300', label: 'Amor' },
    { type: 'fire', Icon: Flame, color: 'text-orange-400 hover:text-orange-300', label: 'Fuego' },
    { type: 'music', Icon: Music, color: 'text-[#d4a853] hover:text-[#e5b963]', label: 'Música' },
  ];

  return (
    <>
      {/* Floating reactions */}
      <div className="absolute right-4 bottom-40 w-16 h-[50vh] pointer-events-none overflow-hidden">
        {reactions.map(({ id, type }) => (
          <FloatingReaction 
            key={id} 
            type={type} 
            id={id}
            onComplete={() => removeReaction(id)} 
          />
        ))}
      </div>

      {/* Reaction buttons */}
      <div 
        data-testid="pulse-reactions-buttons"
        className="absolute right-3 bottom-[180px] flex flex-col gap-2"
        style={{ zIndex: 60 }}
      >
        {buttons.map(({ type, Icon, color, label }) => (
          <button
            key={type}
            data-testid={`reaction-btn-${type}`}
            onClick={() => addReaction(type)}
            disabled={!token || disabled}
            className={`w-11 h-11 rounded-full bg-black/30 backdrop-blur-md border border-white/10 
                       flex items-center justify-center ${color} transition-all duration-200
                       hover:scale-110 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed`}
            aria-label={label}
          >
            <Icon size={20} />
          </button>
        ))}
      </div>

      {/* Animation keyframes */}
      <style>{`
        @keyframes floatUpReaction {
          0% {
            opacity: 1;
            transform: translateY(0) translateX(0) scale(1) rotate(0deg);
          }
          25% {
            opacity: 1;
            transform: translateY(-25vh) translateX(var(--random-x)) scale(1.1) rotate(10deg);
          }
          50% {
            opacity: 0.8;
            transform: translateY(-50vh) translateX(calc(var(--random-x) * -0.5)) scale(1.2) rotate(-5deg);
          }
          75% {
            opacity: 0.4;
            transform: translateY(-75vh) translateX(var(--random-x)) scale(1) rotate(8deg);
          }
          100% {
            opacity: 0;
            transform: translateY(-100vh) translateX(0) scale(0.8) rotate(-10deg);
          }
        }
      `}</style>
    </>
  );
};

/* ── Chat Message Component ── */
const ChatMessage = ({ message, index, total }) => {
  // Older messages are more transparent
  const opacity = 0.5 + (index / total) * 0.5; // 0.5 to 1.0
  
  return (
    <div 
      className="flex items-start gap-2 animate-slideUpChat"
      style={{ 
        opacity,
        animationDelay: `${index * 30}ms`,
      }}
    >
      <div className="flex-1 min-w-0">
        <span className="text-[12px] font-semibold text-white/90 mr-1.5 drop-shadow-md">
          {message.user_name || 'Anon'}
        </span>
        <span className="text-[13px] text-white/80 drop-shadow-sm break-words">
          {message.message}
        </span>
      </div>
    </div>
  );
};

/* ── Fog Chat Overlay (TikTok Style) ── */
export const LiveFogChat = ({ messages = [], onSend, token, lang = 'es' }) => {
  const [inputValue, setInputValue] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const messagesEndRef = useRef(null);
  
  // Filter chat messages and get last 6
  const chatMessages = messages
    .filter(m => m.type === 'chat')
    .slice(-6);

  const handleSend = () => {
    if (inputValue.trim() && onSend) {
      onSend(inputValue.trim());
      setInputValue('');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div 
      data-testid="pulse-fog-chat"
      className="absolute bottom-0 left-0 right-0 pointer-events-none"
      style={{ zIndex: 55 }}
    >
      {/* Fog gradient background */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(to top, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.3) 40%, rgba(0,0,0,0.1) 70%, transparent 100%)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          maskImage: 'linear-gradient(to top, black 0%, black 60%, transparent 100%)',
          WebkitMaskImage: 'linear-gradient(to top, black 0%, black 60%, transparent 100%)',
        }}
      />
      
      {/* Messages container */}
      <div className="relative px-4 pb-2">
        {/* Messages with fade mask */}
        <div 
          className="space-y-2 mb-3 max-h-[30vh] overflow-hidden"
          style={{
            maskImage: 'linear-gradient(to bottom, transparent 0%, black 15%, black 100%)',
            WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 15%, black 100%)',
          }}
        >
          {chatMessages.map((msg, i) => (
            <ChatMessage 
              key={msg.id || i} 
              message={msg} 
              index={i}
              total={chatMessages.length}
            />
          ))}
          <div ref={messagesEndRef} />
        </div>

        {/* Input area */}
        {token && (
          <div className="flex items-center gap-2 pointer-events-auto pb-safe">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-md border border-white/10 
                        flex items-center justify-center text-white/60 hover:text-white hover:bg-white/15 transition"
            >
              <MessageCircle size={18} />
            </button>
            
            <div className="flex-1 flex items-center gap-2 px-4 py-2.5 rounded-full 
                          bg-white/10 backdrop-blur-xl border border-white/10">
              <input
                data-testid="fog-chat-input"
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={lang === 'es' ? 'Escribe algo...' : 'Say something...'}
                className="flex-1 bg-transparent text-[13px] text-white placeholder-white/40 outline-none"
              />
              <button
                data-testid="fog-chat-send"
                onClick={handleSend}
                disabled={!inputValue.trim()}
                className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center 
                          text-white/60 hover:bg-[#d4a853] hover:text-black transition 
                          disabled:opacity-30 disabled:hover:bg-white/10 disabled:hover:text-white/60"
              >
                <Send size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Animation keyframes */}
      <style>{`
        @keyframes slideUpChat {
          from {
            opacity: 0;
            transform: translateY(12px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slideUpChat {
          animation: slideUpChat 0.3s ease-out forwards;
        }
        .pb-safe {
          padding-bottom: max(8px, env(safe-area-inset-bottom));
        }
      `}</style>
    </div>
  );
};

/* ── Combined Export for Mobile ── */
export const LiveMobileChatReactions = ({ 
  messages, 
  onSendChat, 
  onReact, 
  token, 
  lang = 'es',
  disabled = false 
}) => {
  return (
    <>
      <LiveReactionsOverlay onReact={onReact} token={token} disabled={disabled} />
      <LiveFogChat messages={messages} onSend={onSendChat} token={token} lang={lang} />
    </>
  );
};
